import { useCallback, useEffect, useRef, useState } from "react";

export const AUTO_DISMISS_MS = 5_000 as const;

export interface MissedBellBannerState {
  visible: boolean;
  dismiss: () => void;
}

export function useMissedBellBanner(
  isRunning: boolean,
  lastBellFiredAt: number,
): MissedBellBannerState {
  const [visible, setVisible] = useState(false);
  const bellAtHideRef = useRef<number>(lastBellFiredAt);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const dismiss = useCallback(() => {
    setVisible(false);
    if (dismissTimerRef.current) {
      clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  // Clear banner when retreat stops
  useEffect(() => {
    if (!isRunning) {
      dismiss();
    }
  }, [isRunning, dismiss]);

  // Track visibilitychange to detect missed bells
  useEffect(() => {
    if (!isRunning) return;

    const onVisibilityChange = () => {
      if (document.hidden) {
        bellAtHideRef.current = lastBellFiredAt;
      } else {
        if (lastBellFiredAt !== bellAtHideRef.current) {
          setVisible(true);
          if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = setTimeout(() => {
            setVisible(false);
            dismissTimerRef.current = null;
          }, AUTO_DISMISS_MS);
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isRunning, lastBellFiredAt]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  return { visible, dismiss };
}
